import { Box, Flex, Input, Text } from '@chakra-ui/react';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import type { ContractAbiItemInput } from '../types';

import { useI18n } from 'lib/i18n/context';
import useAccount from 'lib/web3/useAccount';
import ClearButton from 'ui/shared/ClearButton';

import { ADDRESS_CONTRACT_KEYS } from '../../../i18n';
import {
  CONTRACT_METHOD_FIELD_INPUT_HEIGHT,
  useContractMethodFieldStyles,
} from './contractMethodFieldStyles';
import useFormatFieldValue from './useFormatFieldValue';
import useValidateField from './useValidateField';

interface Props {
  data: ContractAbiItemInput;
  path: string;
  isDisabled: boolean;
  isOptional?: boolean;
}

const ContractMethodAddressInputField = ({ data, path: name, isDisabled, isOptional }: Props) => {
  const { t } = useI18n();
  const ref = React.useRef<HTMLInputElement>(null);
  const { address } = useAccount();
  const styles = useContractMethodFieldStyles();

  const validate = useValidateField({ isOptional: Boolean(isOptional), argType: data.type, argTypeMatchInt: null });
  const format = useFormatFieldValue({ argType: data.type, argTypeMatchInt: null });

  const { control, setValue } = useFormContext();
  const { field, fieldState } = useController({ control, name, rules: { validate } });

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = format(event.target.value);
    field.onChange(formattedValue);
    setValue(name, formattedValue);
  }, [ field, format, name, setValue ]);

  const handleClear = React.useCallback(() => {
    setValue(name, '');
    ref.current?.focus();
  }, [ name, setValue ]);

  const handleSelfClick = React.useCallback(() => {
    if (!address) {
      return;
    }

    const newValue = format(address);
    setValue(name, newValue, { shouldValidate: true });
  }, [ address, format, name, setValue ]);

  const error = fieldState.error;
  const hasValue = field.value !== undefined && field.value !== '';
  const canUseSelf = Boolean(address) && !isDisabled;

  return (
    <Box w="100%">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        h={ CONTRACT_METHOD_FIELD_INPUT_HEIGHT }
        minH={ CONTRACT_METHOD_FIELD_INPUT_HEIGHT }
        px={ 2 }
        gap={ 2 }
        bg={ styles.inputBg }
        border="1px solid"
        borderColor={ error ? 'error' : styles.inputBorder }
        borderRadius="4px"
        overflow="hidden"
      >
        <Input
          { ...field }
          ref={ ref }
          value={ field.value ?? '' }
          onChange={ handleChange }
          required={ !isOptional }
          placeholder="address"
          autoComplete="off"
          data-1p-ignore
          variant="unstyled"
          h="100%"
          minH="unset"
          flex={ 1 }
          minW={ 0 }
          fontSize="12px"
          lineHeight="24px"
          fontWeight={ 500 }
          color={ styles.inputText }
          _placeholder={{ color: styles.inputPlaceholder }}
          _focusVisible={{ boxShadow: 'none' }}
        />
        <Flex alignItems="center" gap={ 1 } flexShrink={ 0 }>
          { hasValue && <ClearButton onClick={ handleClear } isDisabled={ isDisabled }/> }
          <Text
            as="button"
            type="button"
            fontSize="12px"
            fontWeight={ 500 }
            lineHeight="1"
            color={ canUseSelf ? styles.selfColor : styles.selfDisabledColor }
            bg="transparent"
            border="none"
            p={ 0 }
            cursor={ canUseSelf ? 'pointer' : 'not-allowed' }
            onClick={ handleSelfClick }
            _hover={{ textDecoration: 'none' }}
            _focus={{ outline: 'none' }}
          >
            { t(ADDRESS_CONTRACT_KEYS.actions.self) }
          </Text>
        </Flex>
      </Flex>
      { error && (
        <Box color="error" fontSize="sm" lineHeight={ 5 } mt={ 1 }>
          { error.message }
        </Box>
      ) }
    </Box>
  );
};

export default React.memo(ContractMethodAddressInputField);
